<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { Plus, AlertTriangle } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Switch } from '@/components/shadcnui/switch';
import { Button } from '@/components/shadcnui/button';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Spinner } from '@/components/shadcnui/spinner';
import { Checkbox } from '@/components/shadcnui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/shadcnui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useFw } from '@/composable/useFw';
import { useFwrs } from '@/composable/useFwrs';
import { useChipset } from '@/composable/useChipset';
import { useModel } from '@/composable/useModel';
import { useFwtype } from '@/composable/useFwtype';
// 타입 임포트
import type { fw, release } from '@/types/types';

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadFwList, onReadFwFiles, onAddFw, onUpdateFw, onDeleteFw, onPaginationFw } = useFw();
const { onInUseFw } = useFwrs();
const { onReadChipsetList } = useChipset();
const { onReadModelList } = useModel();
const { onReadFwtypeList } = useFwtype();

// 상태 관리
const isUploadDialogOpen = ref(false);
const isDetailDialogOpen = ref(false);
const selectedFiles = ref<number[]>([]);
const selectedFirmware = ref<any>(null);
const currentPage = ref(1);
const filterChipset = ref(0);
const filterFwType = ref(0);
const filterModelType = ref(0);
const isDragging = ref(false);
const isLoading = ref(true);
const isSpinner = ref(false);
const totalPages = ref(1);
const settingMode = ref(1);
const isuploadckecked = ref(false);
const isDeleteDialogOpen = ref(false);
const inuseReleaseList = ref<release[]>([]);

// 필터링 조건 변경 시 모델 리스트 업데이트
watch([filterChipset, filterFwType, filterModelType], async () => {
  if (!settingMode.value) {
    UserStore.fwfilter.chipset_id = filterChipset.value;
    UserStore.fwfilter.fwtype_id = filterFwType.value;
    UserStore.fwfilter.model_id = filterModelType.value;
    UserStore.fwcurrentPage = 1;
    UserStore.fwfilter.page = 1;
    currentPage.value = 1;
    console.log("필터링 조건 업데이트됨:", UserStore.fwfilter);
    isLoading.value = true;
    totalPages.value = await onPaginationFw(UserStore.fwfilter) ?? 1;
    await onReadFwList(UserStore.fwfilter).finally(() => {
      isLoading.value = false;
    })
  };
});

// FWFileADD 핸들러 함수
const handleAdd = async () => {
  selectedFiles.value = [];
  isUploadDialogOpen.value = true;
  UserStore.fwfilter.page = 1;
  await onReadFwFiles();
};

// 파일 선택 토글 핸들러
const toggleFileSelection = (fileId: number) => {
  const index = selectedFiles.value.indexOf(fileId);
  if (index > -1) selectedFiles.value.splice(index, 1);
  else selectedFiles.value.push(fileId);

  if (selectedFiles.value.length === RSStore.fwFileList.length) {
    isuploadckecked.value = true;
  } else {
    isuploadckecked.value = false;
  }
};
// 전체 선택 토글 핸들러
const handleToggleAllFiles = (checked: boolean) => {
  // .value를 추가하여 실제 데이터 배열에 접근합니다.
  isuploadckecked.value = !isuploadckecked.value;
  if (checked) {
    selectedFiles.value = [];
  } else {
    selectedFiles.value = RSStore.fwFileList.map(f => f.id);
  }
};

// 펌웨어 핸들러
const handleDetail = (fw: fw) => {
  if (!UserStore.fwEdit) {
    return;
  }
  selectedFirmware.value = fw;
  isDetailDialogOpen.value = true;
};
const handleDrop = (e: DragEvent) => {
  isDragging.value = false;
  const files = e.dataTransfer?.files;
  console.log("Dropped files:", files);
};
// 펌웨어 show Update API 호출 핸들러
const handleToggleShow = async (id: number, checked: boolean) => {
  const updatedFw = { id: id, isshow: checked ? 1 : 0 };
  await onUpdateFw(updatedFw);
  await onReadFwList(UserStore.fwfilter)
};

// 다이얼로그 취소 핸들러
const handleCancel = () => {
  isUploadDialogOpen.value = false;
  isDetailDialogOpen.value = false;
  isuploadckecked.value = false;

  // 선택한 파일,펌웨어 초기화
  selectedFiles.value = [];
  selectedFirmware.value = null;
};
// 펌웨어 업로드 API 호출 핸들러
const handleupload = async () => {
  const filesToUpload = RSStore.fwFileList.filter(f => selectedFiles.value.includes(f.id));
  UserStore.fwfilter.page = 1;
  isLoading.value = true;
  await onAddFw(filesToUpload);
  await onReadFwList(UserStore.fwfilter).finally(() => {
    isLoading.value = false;
  });
  isUploadDialogOpen.value = false;
  selectedFiles.value = [];
  isuploadckecked.value = false;
};
const handleDelete = async (id: number) => {
  isSpinner.value = true;
  await onDeleteFw(id);
  await onReadFwList(UserStore.fwfilter);
  isDeleteDialogOpen.value = false;
  isDetailDialogOpen.value = false;
  isSpinner.value = false;
};

// 펌웨어 삭제 다이얼로그 오픈 핸들러 (사용 중인 릴리즈 체크)
const openDeleteDialog = async () => {
  inuseReleaseList.value = await onInUseFw(selectedFirmware.value.id);
  isDeleteDialogOpen.value = true;
};
// 펌웨어 삭제 다이얼로그 클로즈 핸들러 (상태 초기화)
const closeDeleteDialog = () => {
  isDeleteDialogOpen.value = false;
  // 사용중인 펌웨어 및 릴리즈 리스트 초기화
  inuseReleaseList.value = [];
};

// 페이지네이션 핸들러
const paginationHandler = async (page: number) => {
  if (page < 1) {
    page = 1;
  }
  else if (page > totalPages.value) {
    page = totalPages.value;
  }
  currentPage.value = page;
  UserStore.fwfilter.page = page;
  isLoading.value = true;
  await onReadFwList(UserStore.fwfilter).finally(() => {
    isLoading.value = false;
  });
};

onMounted(async () => {
  totalPages.value = await onPaginationFw(UserStore.fwfilter) ?? 1;
  if (UserStore.fwfilter.chipset_id || UserStore.fwfilter.model_id || UserStore.fwfilter.fwtype_id) {
    filterChipset.value = UserStore.fwfilter.chipset_id;
    filterModelType.value = UserStore.fwfilter.model_id;
    filterFwType.value = UserStore.fwfilter.fwtype_id;
  }
  if (UserStore.fwfilter.page) {
    currentPage.value = UserStore.fwfilter.page;
  }
  await onReadChipsetList({ page: 0, size: 0 });
  await onReadModelList({ chipset_id: 0, page: 0, size: 0 });
  await onReadFwtypeList({ page: 0, size: 0 });
  await onReadFwList(UserStore.fwfilter).finally(() => {
    isLoading.value = false;
  });
  settingMode.value = 0;
});
</script>

<template>
  <div class="p-8">
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-3xl font-bold text-foreground">펌웨어</h1>
      <div class="flex gap-4 mb-2">
        <div class="w-48">
          <Label class="mb-2 block">Chipset</Label>
          <Select v-model="filterChipset">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
                {{ chipset.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="w-48">
          <Label class="mb-2 block">Model</Label>
          <Select v-model="filterModelType">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="model in RSStore.modelList" :key="model.id" :value="model.id">
                {{ model.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="w-48">
          <Label class="mb-2 block">Firmware Type</Label>
          <Select v-model="filterFwType">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="fwtype in RSStore.fwtypeList" :key="fwtype.id" :value="fwtype.id">
                {{ fwtype.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="flex items-end">
          <Button @click="handleAdd" :disabled="!UserStore.fwAdd">
            <Plus class="mr-2 h-4 w-4" /> Upload
          </Button>
        </div>
      </div>
    </div>
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 ">
            <TableHead class="px-5">ID</TableHead>
            <TableHead>Chipset</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Target</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Added Date</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>DL Count</TableHead>
            <TableHead>Show</TableHead>
            <TableHead>Download</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태  -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="11" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <!-- 데이터 목록 -->
          <template v-else>
            <TableRow v-for="fw in RSStore.fwList" :key="fw.id" class="cursor-pointer hover:bg-accent/50"
              @click="handleDetail(fw)">
              <TableCell class="px-5">{{ fw.id }}</TableCell>
              <TableCell>{{RSStore.chipsetList.find(c => c.id === fw.chipset_id)?.name}}</TableCell>
              <TableCell>{{RSStore.modelList.find(m => m.id === fw.model_id)?.name}}</TableCell>
              <TableCell>{{ fw.tgvername }}</TableCell>
              <TableCell>{{ fw.fwvername }}</TableCell>
              <TableCell>{{RSStore.fwtypeList.find(ft => ft.id === fw.fwtype_id)?.name}}</TableCell>
              <TableCell>{{ new Date(fw.addeddate).toLocaleDateString('ko-KR', {
                year: 'numeric', month: '2-digit', day:
                  '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
              }) }}</TableCell>
              <TableCell>{{ (fw.size / (1024 * 1024)).toFixed(2) }} MB</TableCell>
              <TableCell>{{ fw.dlcount }}</TableCell>
              <TableCell @click.stop>
                <Switch :model-value="Boolean(fw.isshow)" @update:model-value="handleToggleShow(fw.id, $event)"
                  :disabled="!UserStore.fwEdit" />
              </TableCell>
              <TableCell class="px-6">
                <a :href="fw.link" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline"
                  @click.stop>
                  Link
                </a>
              </TableCell>
            </TableRow>
            <TableRow v-for="i in UserStore.itemPerPage - RSStore.fwList.length" :key="'empty-' + i">
              <TableCell colspan="11">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalPages * UserStore.itemPerPage"
        :items-per-page="UserStore.itemPerPage" :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
    </div>

    <!-- 펌웨어 업로드 다이얼로그 -->
    <Dialog v-model:open="isUploadDialogOpen">
      <DialogContent class="sm:max-w-[900px] max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>펌웨어 업로드</DialogTitle>
          <DialogDescription>
          </DialogDescription>
        </DialogHeader>
        <div class="py-4">
          <div class="border rounded-lg overflow-hidden relative transition-all "
            :class="isDragging ? 'border-primary border-2 bg-primary/5' : ''" @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
            <Table>
              <TableHeader>
                <TableRow class="bg-muted">
                  <TableHead class="w-12">
                    <Checkbox :model-value="isuploadckecked"
                      @update:model-value="handleToggleAllFiles(isuploadckecked)" />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead class="w-28">Size</TableHead>
                  <TableHead class="w-20">Hash</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow v-for="file in RSStore.fwFileList" :key="file.id" class="cursor-pointer"
                  @click="toggleFileSelection(file.id)">
                  <TableCell @click.stop>
                    <Checkbox :model-value="selectedFiles.includes(file.id)"
                      @update:model-value="() => toggleFileSelection(file.id)" />
                  </TableCell>
                  <TableCell class="font-mono text-xs break-all text-foreground">{{ file.name }}</TableCell>
                  <TableCell>{{ file.size }}</TableCell>
                  <TableCell> {{ file.md5 ? 'O' : 'X' }} </TableCell>
                </TableRow>
                <TableRow v-for="i in 10 - RSStore.fwFileList.length" :key="'empty-' + i">
                  <TableCell colspan="11">&nbsp;</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="handleCancel">Cancel</Button>
          <Button :disabled="selectedFiles.length === 0" @click="handleupload">ADD</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- 펌웨어 상세보기 다이얼로그 -->
    <Dialog v-model:open="isDetailDialogOpen">
      <DialogContent class="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>상세보기</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div v-if="selectedFirmware" class="py-4 space-y-4">
          <div class="grid grid-cols-[140px_1fr] items-center gap-4 ">
            <Label class="text-right capitalize">{{ "chipset" }}</Label>
            <Input :model-value="RSStore.chipsetList.find(c => c.id === selectedFirmware.chipset_id)?.name" disabled />
            <Label class="text-right capitalize">{{ "model" }}</Label>
            <Input :model-value="RSStore.modelList.find(m => m.id === selectedFirmware.model_id)?.name" disabled />
            <Label class="text-right capitalize">{{ "target version" }}</Label>
            <Input :model-value="selectedFirmware.tgvername" disabled />
            <Label class="text-right capitalize">{{ "firmware version" }}</Label>
            <Input :model-value="selectedFirmware.fwvername" disabled />
            <Label class="text-right capitalize">{{ "firmware type" }}</Label>
            <Input :model-value="RSStore.fwtypeList.find(ft => ft.id === selectedFirmware.fwtype_id)?.name" disabled />
            <Label class="text-right capitalize">{{ "MD5" }}</Label>
            <Input :model-value="selectedFirmware.md5" disabled />
            <Label class="text-right capitalize">{{ "added date" }}</Label>
            <Input :model-value="selectedFirmware.addeddate" disabled />
            <Label class="text-right capitalize">{{ "size" }}</Label>
            <Input :model-value="(selectedFirmware.size / (1024 * 1024)).toFixed(2) + ' MB'" disabled />
            <Label class="text-right capitalize">{{ "download count" }}</Label>
            <Input :model-value="selectedFirmware.dlcount" disabled />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="handleCancel">Cancel</Button>
          <Button v-if="UserStore.fwDelete" variant="destructive" @click="openDeleteDialog"
            :disabled="!UserStore.fwDelete">
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- 펌웨어 삭제 확인창 -->
    <Dialog v-model:open="isDeleteDialogOpen" @update:open="closeDeleteDialog">
      <form>
        <!-- 릴리즈에서 사용중인 펌웨어가 있을 경우 -->
        <DialogContent v-if="inuseReleaseList.length > 0"
          class="sm:max-w-[500px] sm:max-h-[90%] sm:max-h-[90%] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              <div class="flex items-center gap-2">
                <AlertTriangle class="w-6 h-6 text-red-500" />
                펌웨어 삭제
              </div>
            </DialogTitle>
            <hr class="my-2 bg-" />
            <DialogDescription style="white-space: pre-line;">
              선택한 펌웨어는 현재 사용 중인 릴리즈가 존재하여 삭제가 불가능합니다. <br /> <br />
              사용중인 릴리즈 목록<br />
              <div class="border rounded-lg bg-card overflow-hidden">
                <Table class="">
                  <TableHeader>
                    <TableRow class="bg-muted/50 ">
                      <TableHead class="px-5 w-20">ID</TableHead>
                      <TableHead>Title</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow v-for="release in inuseReleaseList" :key="release.id">
                      <TableCell class="px-5 w-20">{{ release.id }}</TableCell>
                      <TableCell class="max-w-[0] truncate">{{ release.title }}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" @click="closeDeleteDialog">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>

        <!-- 릴리즈에서 사용중인 펌웨어가 없을 경우 -->
        <DialogContent v-else class="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              <div class="flex items-center gap-2">
                <AlertTriangle class="w-6 h-6 text-red-500" />
                펌웨어 삭제
              </div>
            </DialogTitle>
            <hr class="my-2 bg-" />
            <DialogDescription style="white-space: pre-line;">
              <div v-if="selectedFirmware" class="my-4 rounded-md border bg-muted/40 p-4 space-y-3 text-sm">
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Chipset</span>
                  <span class="text-muted-foreground font-medium">
                    {{RSStore.chipsetList.find(c => c.id === selectedFirmware.chipset_id)?.name || '-'}}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Model</span>
                  <span class="text-muted-foreground font-medium">
                    {{RSStore.modelList.find(m => m.id === selectedFirmware.model_id)?.name || '-'}}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Target Version</span>
                  <span class="text-muted-foreground font-medium">
                    {{ selectedFirmware.tgvername || '-' }}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Firmware Version</span>
                  <span class="text-muted-foreground font-medium">
                    {{ selectedFirmware.fwvername || '-' }}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Firmware Type</span>
                  <span class="text-muted-foreground font-medium">
                    {{RSStore.fwtypeList.find(ft => ft.id === selectedFirmware.fwtype_id)?.name || '-'}}
                  </span>
                </div>
              </div>
              <p class="text-muted-foreground text-red-500">
                펌웨어를 삭제하시겠습니까?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" @click="closeDeleteDialog">
              Cancel
            </Button>
            <template v-if="!isSpinner">
              <Button @click="handleDelete(selectedFirmware.id)" :disabled="isSpinner" class="w-[80px]">
                Confirm
              </Button>
            </template>
            <template v-else>
              <Button class="w-[80px]" disabled>
                <Spinner class="mx-auto h-5 w-5" />
              </Button>
            </template>
          </DialogFooter>
        </DialogContent>
      </form>
    </Dialog>
  </div>
</template>